// src/components/Layout/MobileBottomNav.jsx
import React from 'react';
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Badge,
} from '@mui/material';
import {
  Home,
  Search,
  Movie,
  Tv,
  Chat,
  RssFeed,
  Article,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { showAuthAlert } from '../../utils/authAlerts';

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { unreadCount } = useChat();
  const { user } = useAuth();
  const { addNotification } = useApp();

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/' || path.startsWith('/movies')) return 0;
    if (path.startsWith('/shows')) return 1;
    if (path.startsWith('/news')) return 2;
    if (path.startsWith('/search')) return 3;
    if (path.startsWith('/feed')) return 4;
    if (path.startsWith('/chat')) return 5;
    return 0;
  };

  const handleChange = (event, newValue) => {
    switch (newValue) {
      case 0:
        navigate('/movies');
        break;
      case 1:
        navigate('/shows');
        break;
      case 2:
        navigate('/news');
        break;
      case 3:
        if (user) {
          navigate('/search');
        } else {
          showAuthAlert(addNotification, navigate, 'SEARCH');
        }
        break;
      case 4:
        if (user) {
          navigate('/feed');
        } else {
          showAuthAlert(addNotification, navigate, 'FEED');
        }
        break;
      case 5:
        if (user) {
          navigate('/chat');
        } else {
          showAuthAlert(addNotification, navigate, 'CHAT');
        }
        break;
      default:
        break;
    }
  };

  return (
    <Paper
      sx={{
        position: 'static', // Let CSS handle positioning
        borderTop: '1px solid',
        borderColor: 'divider',
        width: '100%',
        maxWidth: '100vw',
        '& .MuiBottomNavigationAction-root': {
          minWidth: 'auto',
          padding: '8px 12px',
        },
      }}
      elevation={8}
    >
      <BottomNavigation
        value={getActiveTab()}
        onChange={handleChange}
        showLabels
        sx={{
          minHeight: 75,
          height: 'auto',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.9) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderTop: theme.palette.mode === 'dark'
            ? '1px solid rgba(255,255,255,0.1)'
            : '1px solid rgba(0,0,0,0.1)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 -4px 20px rgba(0,0,0,0.5)'
            : '0 -4px 20px rgba(0,0,0,0.1)',
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.7rem',
            fontWeight: 500,
            marginTop: '2px',
          },
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 8px 8px',
            borderRadius: '12px',
            margin: '4px 2px',
            transition: 'all 0.3s ease',
            '&.Mui-selected': {
              color: 'primary.main',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 193, 7, 0.15)'
                : 'rgba(25, 118, 210, 0.15)',
              transform: 'translateY(-2px)',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.1)',
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.05)',
            },
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.4rem',
            transition: 'transform 0.3s ease',
          },
        }}
      >
        <BottomNavigationAction
          label="Movies"
          icon={<Movie sx={{ fontSize: '1.6rem' }} />}
          sx={{
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              fontWeight: 600,
            },
            '&.Mui-selected': {
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.8rem',
                fontWeight: 700,
              },
            },
          }}
        />
        <BottomNavigationAction
          label="Shows"
          icon={<Tv sx={{ fontSize: '1.6rem' }} />}
          sx={{
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              fontWeight: 600,
            },
            '&.Mui-selected': {
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.8rem',
                fontWeight: 700,
              },
            },
          }}
        />
        <BottomNavigationAction
          label="News"
          icon={<Article sx={{ fontSize: '1.6rem' }} />}
          sx={{
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              fontWeight: 600,
            },
            '&.Mui-selected': {
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.8rem',
                fontWeight: 700,
              },
            },
          }}
        />
        <BottomNavigationAction
          label="Search"
          icon={<Search />}
        />
        <BottomNavigationAction
          label="Feed"
          icon={<RssFeed />}
        />
        <BottomNavigationAction
          label="Chat"
          icon={
            <Badge 
              badgeContent={unreadCount > 0 ? unreadCount : null} 
              color="error" 
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  minWidth: '16px',
                  height: '16px',
                }
              }}
            >
              <Chat />
            </Badge>
          }
        />
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;